// api/upload-run.js — 업로드 **한 번의 진행**을 끝까지 몰고 가는 자리.
//
// `upload.js` 가 「어떤 상태에서 무엇이 다음인가」를 정하고, 여기서는 그 걸음들을 실제로
// 밟는다. 네트워크를 부르지 않는다 — **전부 주입받는다**(`deps`). 그래서 이 파일은
// 브라우저 없이도, 응답을 우리가 붙들었다 놓는 방식으로 실제 진행을 시험할 수 있다.
//
// ## 왜 컴포넌트에서 떼어 냈나
//
// 독립 검토(2026-09-12 2차)의 2번: **await 사이사이에 컴포넌트가 사라질 수 있다.**
// 대화상자를 닫거나 Space·Node 를 바꾸면 화면은 없어지는데, 이미 떠난 digest·예약·확정은
// 계속 진행된다. 그 결과가 돌아와 **다른 자리의 초안**에 써지면, 사람은 자기가 열지도
// 않은 곳에서 남의 업로드 상태를 보게 된다.
//
// 그래서 이 진행에는 **울타리(`alive`)** 가 있다. 모든 await 뒤에서 울타리를 확인하고,
// 울타리가 무너졌으면 **아무것도 쓰지 않고** 조용히 끝낸다. 상태를 되돌리는 일은
// 울타리를 무너뜨린 쪽(unmount·범위 변경)이 자기 자리에서 한다.
//
// ## 잃지 않는 것
//
//   · 예약 본문 — 한 번 보낸 뒤로는 그대로 쓴다(같은 멱등 키에 다른 본문 = 영원한 거절).
//   · uploadId — 확정 응답을 못 받았다고 버리지 않는다. 같은 키로 다시 부르면 서버가
//     첫 결과를 그대로 준다. 버리면 같은 파일을 또 올리게 된다.
//   · manifest — 붙이기가 실패해도 판은 이미 존재한다. 다시 올리는 일은 없다.

import {
  advanceUpload, emptyUploadState, resumeStep, resumeStepAfterError, UPLOAD_STEP,
} from './upload.js';

/** 울타리가 무너졌을 때 내부에서만 쓰는 신호. 밖으로 던지지 않는다. */
const FENCE = Symbol('upload-fence');

export const RUN_OUTCOME = Object.freeze({
  DONE: 'done',
  FAILED: 'failed',
  /** 울타리가 무너졌거나 사람이 멈췄다. **실패가 아니다** — 아무것도 쓰지 않았다. */
  ABANDONED: 'abandoned',
});

/**
 * 예약에 보낼 본문. **이미 보낸 적이 있으면 그것을 그대로 쓴다.**
 *
 * 이 한 줄이 「응답을 잃은 예약 + 바뀐 제목 = 영구 실패」를 막는다.
 */
export function reservationPayload(state, intent) {
  return state?.reservedIntent ?? intent;
}

/** 지금 사람이 적어 둔 것이 이미 예약한 본문과 다른가 — 화면이 그 사실을 말할 자리. */
export function intentDiffers(state, intent) {
  const held = state?.reservedIntent;
  if (!held || !intent) return false;
  return ['title', 'filename', 'mediaType', 'kind', 'byteSize', 'assetId'].some(
    (field) => (held[field] ?? null) !== (intent[field] ?? null),
  );
}

/**
 * 예약한 자리를 **놓아 준다.**
 *
 * 파일을 바꾸거나 지울 때 부른다. 이걸 빠뜨리면 서버에는 아무도 쓰지 않을 예약이 남고,
 * 그 크기만큼 Space 의 보관 공간이 TTL 이 지날 때까지 잠긴다.
 *
 * **확정된 업로드는 놓지 않는다.** 그건 이미 판이고, 취소로 사라지지 않는다(서버도
 * `upload_already_finalized` 로 거절한다). 파일 선택을 지운다고 올라간 파일을 지우지 않는다.
 */
export async function releaseReservation(state, deps) {
  const uploadId = state?.uploadId ?? null;
  if (!uploadId || state?.manifest) return { released: false, reason: state?.manifest ? 'finalized' : 'nothing' };
  try {
    await deps.abort({ uploadId, idempotencyKey: deps.newKey() });
    return { released: true };
  } catch (error) {
    // 놓기에 실패해도 사람에게 다시 물을 일은 아니다 — 예약은 TTL 이 지나면 닫힌다.
    return { released: false, reason: 'failed', error };
  }
}

/**
 * 한 번의 진행.
 *
 * @param from   시작할 걸음. 없으면 상태가 정한다.
 * @param alive  울타리. `false` 가 되는 순간부터 **아무것도 쓰지 않는다**.
 * @param emit   상태 사건 하나. 울타리 안에서만 불린다.
 * @param deps   `{ digest, reserve, transfer, finalize, attach?, abort, newKey, describe? }`
 * @returns `{ outcome, state, error? }`
 */
export async function runUpload({
  from = null,
  state = emptyUploadState(),
  intent,
  file,
  deps,
  emit = () => {},
  alive = () => true,
  signal = null,
}) {
  let current = state;

  const publish = (event) => {
    if (!alive()) throw FENCE;
    current = advanceUpload(current, event);
    emit(event, current);
    return current;
  };
  // 모든 await 뒤에 선다. 여기를 지나지 못하면 그 결과는 **버려진다** — 이미 없는 자리의 값이다.
  const checkpoint = () => {
    if (!alive()) throw FENCE;
  };

  let at = from ?? resumeStep(current);
  if (at === null) return { outcome: RUN_OUTCOME.DONE, state: current };

  try {
    if (at === UPLOAD_STEP.RESERVE) {
      publish({ type: 'digesting' });
      const checksumSha256 = await deps.digest(file);
      checkpoint();

      const key = current.reserveKey ?? deps.newKey();
      // 본문은 여기서 굳는다. 이 뒤로 제목이 바뀌어도 이 예약은 이 본문으로만 말한다.
      const payload = { ...reservationPayload(current, intent), checksumSha256 };
      publish({ type: 'reserving', key, intent: payload });

      const reservation = await deps.reserve(payload, { idempotencyKey: key });
      checkpoint();
      publish({
        type: 'reserved',
        uploadId: reservation.uploadId,
        checksumSha256: reservation.checksumSha256 ?? checksumSha256,
        contentPath: reservation.contentPath ?? null,
        finalizePath: reservation.finalizePath ?? null,
      });
      at = UPLOAD_STEP.TRANSFER;
    }

    if (at === UPLOAD_STEP.TRANSFER) {
      await deps.transfer({
        uploadId: current.uploadId,
        contentPath: current.contentPath,
        file,
        signal,
        onProgress: ({ loaded }) => {
          // 진행률은 울타리가 살아 있을 때만 그린다. 죽은 자리에 % 를 쓰지 않는다.
          if (!alive()) return;
          current = advanceUpload(current, { type: 'progress', loaded });
          emit({ type: 'progress', loaded }, current);
        },
      });
      checkpoint();
      publish({ type: 'transferred' });
      at = UPLOAD_STEP.FINALIZE;
    }

    if (at === UPLOAD_STEP.FINALIZE) {
      const key = current.finalizeKey ?? deps.newKey();
      publish({ type: 'finalizing', key });
      const manifest = await deps.finalize({
        uploadId: current.uploadId,
        finalizePath: current.finalizePath,
        idempotencyKey: key,
      });
      checkpoint();
      publish({ type: 'finalized', manifest });
      at = UPLOAD_STEP.ATTACH;
    }

    if (at === UPLOAD_STEP.ATTACH) {
      if (typeof deps.attach === 'function') {
        const ok = await deps.attach(current.manifest);
        checkpoint();
        if (!ok) {
          // **판은 그대로 둔다.** 남은 일은 참조 하나를 만드는 것뿐이고, 그건 다시 할 수 있다.
          publish({
            type: 'failed',
            step: UPLOAD_STEP.ATTACH,
            error: { code: 'attach_failed', message: null },
          });
          return { outcome: RUN_OUTCOME.FAILED, state: current };
        }
      }
      publish({ type: 'attached' });
      return { outcome: RUN_OUTCOME.DONE, state: current, manifest: current.manifest };
    }

    return { outcome: RUN_OUTCOME.DONE, state: current };
  } catch (error) {
    if (error === FENCE || !alive()) return { outcome: RUN_OUTCOME.ABANDONED, state: current };

    const code = error?.code ?? null;
    if (code === 'upload_cancelled') {
      // 사람이 멈춘 것이다. 상태는 멈춘 쪽이 이미 되돌렸다.
      return { outcome: RUN_OUTCOME.ABANDONED, state: current };
    }
    // 적용한 것과 알리는 것이 **같은 사건**이어야 한다. 둘이 갈리면 화면의 초안과
    // 여기의 상태가 서로 다른 것을 믿게 된다.
    const failure = {
      type: 'failed',
      step: resumeStepAfterError(current, code),
      error: { code, message: deps.describe?.(error) ?? error?.message ?? null },
    };
    current = advanceUpload(current, failure);
    emit(failure, current);
    return { outcome: RUN_OUTCOME.FAILED, state: current, error };
  }
}
